import {
  type CreativeMedia,
  creativeMediaSchema,
  valueListSchema,
} from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-meta";
import { VALUE_SCHEMAS } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-query";
import { format, parseISO } from "date-fns";
import { tupleOf } from "@hightouch/lib/util/zod-tuple";
import { z } from "zod/v4";
import type { FatigueCountsState } from "src/pages/ad-studio/insights/shared/types";
import {
  drillTileMetricsOf,
  fatigueMetricsOf,
} from "src/pages/ad-studio/insights/performance/utils/meta";
import type {
  AccountOption,
  AdHeaderExtras,
  AdMedia,
  AdRow,
  AdViewMeta,
  DetailedAd,
  PilledAdRow,
  AdSetRow,
  AdSetViewMeta,
  CampaignViewMeta,
  CampaignRow,
  DailyPoint,
  FatiguePage,
  Found,
  AdHeaderRow,
  ParentHeaderRow,
  GroupDimension,
  GroupRow,
  GroupPreviewAd,
  GroupValue,
  EntityStatus,
  FatigueRow,
  MetricCell,
  MetricColumn,
  Objective,
  Paged,
  Reported,
  ValuesByMember,
  ViewMeta,
  NonEmptyArray,
  EventOption,
  EventsByChannel,
} from "src/pages/ad-studio/insights/performance/types";

export const text = z.string().nullable();
export const numeral = VALUE_SCHEMAS.number
  .nullable()
  .transform(
    (value): Reported => (value === null ? "not-reported" : Number(value)),
  );

type Column<Value extends z.ZodType = z.ZodType> = {
  member: string;
  value: Value;
};
type Columns = Record<string, Column>;
type ValuesOf<C extends Columns> = { [K in keyof C]: z.output<C[K]["value"]> };

export const column = <Value extends z.ZodType>(
  member: string,
  value: Value,
): Column<Value> => ({ member, value });

const wireShapeOf = (columns: Columns): Record<string, z.ZodType> =>
  Object.fromEntries(
    Object.values(columns).map(({ member, value }) => [member, value]),
  );

// Object.fromEntries drops the key-to-leaf pairing `columns` spells out.
const valuesOf = <C extends Columns>({
  columns,
  wire,
}: {
  columns: C;
  wire: Record<string, unknown>;
}): ValuesOf<C> =>
  Object.fromEntries(
    Object.entries(columns).map(([key, { member }]) => [key, wire[member]]),
  ) as ValuesOf<C>;

export const rowSchema = <C extends Columns, Row>({
  columns,
  toRow,
}: {
  columns: C;
  toRow: (values: ValuesOf<C>) => Row;
}): z.ZodType<Row, unknown> =>
  z
    .object(wireShapeOf(columns))
    .transform((wire) => toRow(valuesOf({ columns, wire })));

const measuredRowSchema = <C extends Columns, Row>({
  columns,
  metrics,
  toRow,
}: {
  columns: C;
  metrics: readonly MetricColumn[];
  toRow: (values: ValuesOf<C>, cells: readonly MetricCell[]) => Row;
}): z.ZodType<Row, unknown> =>
  z
    .object({
      ...wireShapeOf(columns),
      ...Object.fromEntries(metrics.map(({ member }) => [member, numeral])),
    })
    .transform((wire) =>
      toRow(
        valuesOf({ columns, wire }),
        metrics.map((metric) => ({
          column: metric,
          // The object shape parsed every metric member with `numeral`.
          value: wire[metric.member] as Reported,
        })),
      ),
    );

type Measured<View extends ViewMeta = ViewMeta> = {
  view: View;
  metrics: readonly MetricColumn[];
};

const listedValue = ({ view, member }: { view: ViewMeta; member: string }) =>
  z
    .string()
    .refine((value) => view.valueLabels.get(member)?.has(value) === true, {
      error: (issue) => `${member} lists no value ${String(issue.input)}`,
    });

const paged = <Row>(
  row: z.ZodType<Row, unknown>,
): z.ZodType<Paged<Row>, unknown> =>
  z
    .object({ data: z.array(row), total: z.number().int().min(0) })
    .transform(({ data, total }) => ({ rows: data, total }));

export const toAdMedia = (creatives: readonly CreativeMedia[]): AdMedia => {
  const [first, ...rest] = creatives;
  return first === undefined
    ? { kind: "no-media" }
    : { kind: "media", creatives: [first, ...rest] };
};

const toEntityStatus = (value: string | null): EntityStatus =>
  value === null ? { kind: "no-status" } : { kind: "status", value };

const toObjective = (text: string | null): Objective =>
  text === null ? { kind: "no-objective" } : { kind: "objective", text };

const adDimensionColumns = (view: AdViewMeta) => {
  const { card, table, fatigue } = view.layouts;
  return {
    syncId: column(view.controls.channelPicker.sync, z.string()),
    entityId: column(view.entities.entity.id, z.string()),
    entityName: column(view.entities.entity.name, z.string()),
    status: column(table.status, text),
    media: column(card.media, creativeMediaSchema),
    creativeCount: column(
      card.creatives,
      VALUE_SCHEMAS.number.transform(Number),
    ),
    fatigueStatus: column(
      fatigue.status,
      listedValue({ view, member: fatigue.status }),
    ),
    daysActive: column(fatigue.daysActive, numeral),
  };
};

const pilledAdDimensionColumns = (view: AdViewMeta) => ({
  ...adDimensionColumns(view),
  objective: column(view.layouts.table.objective, text),
});

const detailedAdDimensionColumns = (view: AdViewMeta) => {
  const { fatigue } = view.layouts;
  return {
    ...pilledAdDimensionColumns(view),
    peakCtr: column(fatigue.peakCtr, numeral),
    vsPeak: column(fatigue.vsPeak, numeral),
    wowCtrChange: column(fatigue.wowCtrChange, numeral),
    cause: column(fatigue.cause, text),
  };
};

const membersOf = (columns: Columns): string[] => [
  ...new Set(Object.values(columns).map(({ member }) => member)),
];

export const adDimensions = (view: AdViewMeta): string[] =>
  membersOf(adDimensionColumns(view));

export const pilledAdDimensions = (view: AdViewMeta): string[] =>
  membersOf(pilledAdDimensionColumns(view));

export const detailedAdDimensions = (view: AdViewMeta): string[] =>
  membersOf(detailedAdDimensionColumns(view));

type AdValues = ValuesOf<ReturnType<typeof adDimensionColumns>>;
type DetailedAdValues = ValuesOf<ReturnType<typeof detailedAdDimensionColumns>>;

const toAdFields = (row: AdValues) => ({
  entityType: "ad" as const,
  syncId: row.syncId,
  entityId: row.entityId,
  entityName: row.entityName,
  status: toEntityStatus(row.status),
  media: toAdMedia(row.media),
  creativeCount: row.creativeCount,
});

const toAdRow = ({
  row,
  cells,
}: {
  row: AdValues;
  cells: readonly MetricCell[];
}): AdRow => ({
  ...toAdFields(row),
  metrics: cells,
  // Days active comes from an inner join in the fatigue table, so it is null exactly when the ad has no fatigue row.
  fatigue:
    row.daysActive === "not-reported"
      ? { kind: "no-fatigue-row" }
      : { kind: "reported", status: row.fatigueStatus },
});

const toPilledAdRow = ({
  row,
  cells,
}: {
  row: AdValues & { objective: string | null };
  cells: readonly MetricCell[];
}): PilledAdRow => ({
  ...toAdRow({ row, cells }),
  objective: toObjective(row.objective),
});

const toDetailedAd = (row: DetailedAdValues): DetailedAd => ({
  ...toAdFields(row),
  objective: toObjective(row.objective),
  fatigue:
    row.daysActive === "not-reported"
      ? { kind: "no-fatigue-row" }
      : {
          kind: "reported",
          status: row.fatigueStatus,
          peakCtr: row.peakCtr,
          vsPeakPct: row.vsPeak,
          wowCtrChange: row.wowCtrChange,
          cause:
            row.cause === null
              ? { kind: "no-cause" }
              : { kind: "cause", text: row.cause },
          daysActive: row.daysActive,
        },
});

export const adRowsSchema = ({
  view,
  metrics,
}: Measured<AdViewMeta>): z.ZodType<Paged<AdRow>, unknown> =>
  paged(
    measuredRowSchema({
      columns: adDimensionColumns(view),
      metrics,
      toRow: (row, cells) => toAdRow({ row, cells }),
    }),
  );

export const pilledAdRowsSchema = ({
  view,
  metrics,
}: Measured<AdViewMeta>): z.ZodType<Paged<PilledAdRow>, unknown> =>
  paged(
    measuredRowSchema({
      columns: pilledAdDimensionColumns(view),
      metrics,
      toRow: (row, cells) => toPilledAdRow({ row, cells }),
    }),
  );

export const accountsSchema = (
  view: ViewMeta,
): z.ZodType<ReadonlyMap<string, readonly AccountOption[]>, unknown> => {
  const { sync, account } = view.controls.channelPicker;
  return z
    .object({
      data: z.array(
        rowSchema({
          columns: {
            syncId: column(sync, z.string()),
            id: column(account.member, text),
            name: column(account.name, text),
          },
          toRow: (row) => row,
        }),
      ),
    })
    .transform(
      ({ data }) =>
        new Map(
          [...new Set(data.map(({ syncId }) => syncId))].map((syncId) => [
            syncId,
            data.flatMap((row): AccountOption[] =>
              row.syncId !== syncId || row.id === null
                ? []
                : [
                    {
                      id: row.id,
                      name:
                        row.name === null
                          ? { kind: "not-found" }
                          : { kind: "found", value: row.name },
                    },
                  ],
            ),
          ]),
        ),
    );
};

const entityDimensionColumns = (view: AdSetViewMeta | CampaignViewMeta) => ({
  syncId: column(view.controls.channelPicker.sync, z.string()),
  entityId: column(view.entities.entity.id, z.string()),
  entityName: column(view.entities.entity.name, z.string()),
  status: column(view.layouts.table.status, text),
});

const campaignDimensionColumns = (view: CampaignViewMeta) => ({
  ...entityDimensionColumns(view),
  objective: column(view.layouts.table.objective, text),
});

export const entityDimensions = {
  ad_set: (view: AdSetViewMeta): string[] =>
    membersOf(entityDimensionColumns(view)),
  campaign: (view: CampaignViewMeta): string[] =>
    membersOf(campaignDimensionColumns(view)),
};

const toEntityFields = (
  row: ValuesOf<ReturnType<typeof entityDimensionColumns>>,
) => ({
  syncId: row.syncId,
  entityId: row.entityId,
  entityName: row.entityName,
  status: toEntityStatus(row.status),
});

export const entityRowsSchema = {
  ad_set: ({
    view,
    metrics,
  }: Measured<AdSetViewMeta>): z.ZodType<Paged<AdSetRow>, unknown> =>
    paged(
      measuredRowSchema({
        columns: entityDimensionColumns(view),
        metrics,
        toRow: (row, cells): AdSetRow => ({
          ...toEntityFields(row),
          entityType: "ad_set",
          metrics: cells,
        }),
      }),
    ),
  campaign: ({
    view,
    metrics,
  }: Measured<CampaignViewMeta>): z.ZodType<Paged<CampaignRow>, unknown> =>
    paged(
      measuredRowSchema({
        columns: campaignDimensionColumns(view),
        metrics,
        toRow: (row, cells): CampaignRow => ({
          ...toEntityFields(row),
          entityType: "campaign",
          objective: toObjective(row.objective),
          metrics: cells,
        }),
      }),
    ),
};

const toValueLabel = ({
  dimension,
  raw,
}: {
  dimension: GroupDimension;
  raw: string;
}): string => {
  switch (dimension.kind) {
    case "time": {
      return format(parseISO(raw), "MMM d, yyyy");
    }
    case "listed": {
      return (
        dimension.values.find((option) => option.value === raw)?.label ?? raw
      );
    }
    case "free-text":
    case "queried":
    case "creative-tag": {
      return raw;
    }
    default: {
      return dimension satisfies never;
    }
  }
};

export const groupLabelOf = ({
  dimension,
  value,
}: {
  dimension: GroupDimension;
  value: GroupValue;
}): string => {
  switch (value.kind) {
    case "value": {
      return toValueLabel({ dimension, raw: value.raw });
    }
    case "no-value": {
      return "No value";
    }
    default: {
      return value satisfies never;
    }
  }
};

const previewAd = z
  .object({
    entity_name: z.string(),
    creative_media: creativeMediaSchema,
  })
  .transform(
    ({ entity_name, creative_media }): GroupPreviewAd => ({
      entityName: entity_name,
      media: toAdMedia(creative_media),
    }),
  );

const groupPreviewAdsSchema = z
  .string()
  .transform((raw, context): unknown => {
    try {
      return JSON.parse(raw);
    } catch {
      context.addIssue({
        code: "custom",
        message: "Group previews are not JSON",
      });
      return z.NEVER;
    }
  })
  .pipe(z.tuple([previewAd]).rest(previewAd));

export const groupRowsSchema = ({
  dimension,
  view,
  metrics,
}: Measured<AdViewMeta> & {
  dimension: GroupDimension;
}): z.ZodType<Paged<GroupRow>, unknown> =>
  paged(
    measuredRowSchema({
      columns: {
        value: column(dimension.name, text),
        adCount: column(
          view.layouts.groupCard.count,
          VALUE_SCHEMAS.number.transform(Number),
        ),
        statuses: column(view.layouts.groupCard.statuses, valueListSchema),
        objectives: column(view.layouts.groupCard.objectives, valueListSchema),
        previews: column(
          view.layouts.groupCard.previews,
          groupPreviewAdsSchema,
        ),
      },
      metrics,
      toRow: (row, cells): GroupRow => {
        const value: GroupValue =
          row.value === null
            ? { kind: "no-value" }
            : { kind: "value", raw: row.value };
        return {
          id: row.value === null ? "no-value" : `value:${row.value}`,
          value,
          name:
            dimension.name === view.layouts.groupCard.nameFromAd
              ? { kind: "ad" }
              : {
                  kind: "attribute",
                  label: groupLabelOf({ dimension, value }),
                },
          adCount: row.adCount,
          statuses: row.statuses,
          objectives: row.objectives,
          previews: row.previews,
          metrics: cells,
        };
      },
    }),
  );

export const fatiguePageSchema = (
  view: AdViewMeta,
): z.ZodType<FatiguePage, unknown> => {
  const { ctr, frequency, spend } = view.layouts.fatigue;
  return z
    .object({
      data: z.array(
        measuredRowSchema({
          columns: {
            ...detailedAdDimensionColumns(view),
            ctr: column(ctr, numeral),
            frequency: column(frequency, numeral),
            spend: column(spend, numeral),
            campaign: column(view.entities.campaign.name, text),
          },
          metrics: fatigueMetricsOf(view),
          toRow: (row, cells) => ({
            ad: {
              ...toDetailedAd(row),
              metrics: cells,
              ctr: row.ctr,
              frequency: row.frequency,
              spend: row.spend,
            } satisfies FatigueRow,
            campaign: row.campaign,
          }),
        }),
      ),
      total: z.number().int().min(0),
    })
    .transform(({ data, total }) => ({
      rows: data.map(({ ad }) => ad),
      total,
      campaigns: new Map(
        data.flatMap(({ ad, campaign }) =>
          campaign === null ? [] : [[ad.entityId, campaign] as const],
        ),
      ),
    }));
};

export const fatigueCountsSchema = (
  view: AdViewMeta,
): z.ZodType<FatigueCountsState, unknown> =>
  z
    .object({
      data: z.array(
        rowSchema({
          columns: {
            status: column(
              view.layouts.fatigue.status,
              listedValue({ view, member: view.layouts.fatigue.status }),
            ),
            ads: column(
              view.layouts.groupCard.count,
              VALUE_SCHEMAS.number.transform(Number),
            ),
          },
          toRow: (row) => row,
        }),
      ),
    })
    .transform(({ data }) =>
      view.layouts.fatigue.fatiguing.map((status) => ({
        label:
          view.valueLabels.get(view.layouts.fatigue.status)?.get(status) ??
          status,
        ads: data.find((row) => row.status === status)?.ads ?? 0,
      })),
    );

export const totalSchema = (member: string): z.ZodType<Reported, unknown> =>
  z
    .object({
      data: z.array(
        rowSchema({
          columns: { total: column(member, numeral) },
          toRow: ({ total }) => total,
        }),
      ),
    })
    .transform(({ data: [total] }) => total ?? "not-reported");

const byText = (left: string, right: string) => left.localeCompare(right);

const distinctSorted = (values: readonly (string | null)[]): string[] =>
  [...new Set(values.flatMap((value) => (value === null ? [] : [value])))].sort(
    byText,
  );

const combinationsSchema = <Row>({
  row,
  limit,
}: {
  row: z.ZodType<Row, unknown>;
  limit: number;
}): z.ZodType<readonly Row[], unknown> =>
  z
    .object({
      data: z.array(row).refine((rows) => rows.length < limit, {
        message: "value-combinations-truncated",
      }),
    })
    .transform(({ data }) => data);

export const valuesByMemberSchema = ({
  members: [first, ...rest],
  limit,
}: {
  members: NonEmptyArray<string>;
  limit: number;
}): z.ZodType<ValuesByMember, unknown> => {
  const valuesOf = (member: string) =>
    combinationsSchema({
      row: z.record(z.literal(member), text),
      limit,
    }).transform((rows): readonly [string, string[]] => [
      member,
      distinctSorted(rows.map((row) => row[member] ?? null)),
    ]);
  return tupleOf([valuesOf(first), ...rest.map(valuesOf)]).transform(
    (values) => new Map(values),
  );
};

const toEventOption = ({
  event,
  conversions,
}: {
  event: string;
  conversions: Reported;
}): EventOption =>
  conversions === "not-reported"
    ? { kind: "not-counted", event }
    : { kind: "counted", event, conversions };

export const eventsByChannelSchema = ({
  channel,
  event,
  conversions,
  limit,
}: {
  channel: string;
  event: string;
  conversions: string;
  limit: number;
}): z.ZodType<EventsByChannel, unknown> =>
  combinationsSchema({
    row: rowSchema({
      columns: {
        channel: column(channel, text),
        event: column(event, text),
        conversions: column(conversions, numeral),
      },
      toRow: (row) => row,
    }),
    limit,
  }).transform(
    (rows) =>
      new Map(
        distinctSorted(rows.map((row) => row.channel)).map((key) => [
          key,
          rows
            .flatMap((row) =>
              row.channel === key && row.event !== null
                ? [
                    toEventOption({
                      event: row.event,
                      conversions: row.conversions,
                    }),
                  ]
                : [],
            )
            .sort((left, right) => byText(left.event, right.event)),
        ]),
      ),
  );

const first = <Row>(
  row: z.ZodType<Row, unknown>,
): z.ZodType<Found<Row>, unknown> =>
  z
    .object({ data: z.array(row).max(1) })
    .transform(
      ({ data: [head] }): Found<Row> =>
        head === undefined
          ? { kind: "not-found" }
          : { kind: "found", value: head },
    );

const toFound = (value: string | null): Found<string> =>
  value === null ? { kind: "not-found" } : { kind: "found", value };

export const tagCategoriesOf = (view: AdViewMeta) =>
  view.folders
    .flatMap((folder) => folder.members)
    .flatMap((member) => (member.kind === "creative-tag" ? [member] : []));

export type AdTagCategory = ReturnType<typeof tagCategoriesOf>[number];

export const adTagsSchema = ({
  categories: [first, ...rest],
  limit,
}: {
  categories: NonEmptyArray<AdTagCategory>;
  limit: number;
}): z.ZodType<readonly string[], unknown> => {
  const tagsOf = (category: AdTagCategory) =>
    combinationsSchema({
      row: rowSchema({
        columns: { value: column(category.name, text) },
        toRow: ({ value }) => value,
      }),
      limit,
    }).transform((values) =>
      distinctSorted(values).map((value) => `${category.shortTitle}: ${value}`),
    );
  return tupleOf([tagsOf(first), ...rest.map(tagsOf)]).transform((tags) =>
    tags.flat(),
  );
};

const adHeaderDimensionColumns = (view: AdViewMeta) => {
  const { copy } = view.layouts.drawer;
  return {
    ...detailedAdDimensionColumns(view),
    adSetId: column(view.entities.adSet.id, text),
    adSetName: column(view.entities.adSet.name, text),
    campaignName: column(view.entities.campaign.name, text),
    primaryText: column(copy.primaryText, text),
    headline: column(copy.headline, text),
    description: column(copy.description, text),
    callToAction: column(copy.callToAction, text),
    destinationUrl: column(copy.destinationUrl, text),
  };
};

const adSetHeaderDimensionColumns = (view: AdSetViewMeta) => ({
  ...entityDimensionColumns(view),
  campaignId: column(view.entities.campaign.id, text),
});

export const headerDimensions = {
  ad: (view: AdViewMeta): string[] => membersOf(adHeaderDimensionColumns(view)),
  ad_set: (view: AdSetViewMeta): string[] =>
    membersOf(adSetHeaderDimensionColumns(view)),
  campaign: (view: CampaignViewMeta): string[] =>
    membersOf(campaignDimensionColumns(view)),
};

type AdHeaderValues = ValuesOf<ReturnType<typeof adHeaderDimensionColumns>>;

const toCreativeCopy = (
  row: AdHeaderValues,
): AdHeaderExtras["creativeCopy"] => ({
  primaryText: toFound(row.primaryText),
  headline: toFound(row.headline),
  description: toFound(row.description),
  callToAction: toFound(row.callToAction),
  destinationUrl: toFound(row.destinationUrl),
});

export const adHeaderSchema = (
  view: AdViewMeta,
): z.ZodType<Found<AdHeaderRow>, unknown> =>
  first(
    measuredRowSchema({
      columns: adHeaderDimensionColumns(view),
      metrics: drillTileMetricsOf(view),
      toRow: (row, tiles): AdHeaderRow => ({
        row: { ...toDetailedAd(row), tiles },
        extras: {
          adSetId: toFound(row.adSetId),
          adSetName: toFound(row.adSetName),
          campaignName: toFound(row.campaignName),
          creativeCopy: toCreativeCopy(row),
        },
      }),
    }),
  );

export const adSetHeaderSchema = (
  view: AdSetViewMeta,
): z.ZodType<Found<ParentHeaderRow>, unknown> =>
  first(
    measuredRowSchema({
      columns: adSetHeaderDimensionColumns(view),
      metrics: drillTileMetricsOf(view),
      toRow: (row, tiles): ParentHeaderRow => ({
        grain: "ad_set",
        row: { ...toEntityFields(row), entityType: "ad_set", tiles },
        campaignId: toFound(row.campaignId),
      }),
    }),
  );

export const campaignHeaderSchema = (
  view: CampaignViewMeta,
): z.ZodType<Found<ParentHeaderRow>, unknown> =>
  first(
    measuredRowSchema({
      columns: campaignDimensionColumns(view),
      metrics: drillTileMetricsOf(view),
      toRow: (row, tiles): ParentHeaderRow => ({
        grain: "campaign",
        row: {
          ...toEntityFields(row),
          entityType: "campaign",
          objective: toObjective(row.objective),
          tiles,
        },
      }),
    }),
  );

export const dailyColumns = (view: AdViewMeta) => {
  const { ctr, frequency } = view.layouts.fatigue;
  return {
    date: column(`${view.controls.dateRange.date}.day`, z.string()),
    ctr: column(ctr, numeral),
    frequency: column(frequency, numeral),
  };
};

export const dailySchema = (
  view: AdViewMeta,
): z.ZodType<readonly DailyPoint[], unknown> =>
  z
    .object({
      data: z.array(
        rowSchema({
          columns: dailyColumns(view),
          toRow: (row): DailyPoint => ({
            date: row.date.slice(0, 10),
            ctr: row.ctr,
            frequency: row.frequency,
          }),
        }),
      ),
    })
    .transform(({ data }) => data);

export const rowCountSchema: z.ZodType<number, unknown> = z
  .object({ total: z.number().int().min(0) })
  .transform(({ total }) => total);

export const aggregateSchema = (
  metrics: readonly MetricColumn[],
): z.ZodType<Found<readonly MetricCell[]>, unknown> =>
  first(
    measuredRowSchema({
      columns: {},
      metrics,
      toRow: (_, cells) => cells,
    }),
  );
